import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/dates";

type AnyRow = Record<string, any>;

const RPC_NAME = "get_recent_dcp_for_driver";
const RPC_LOOKBACK_DAYS = 60;

const fmtMoney = (n: number | null | undefined) =>
  typeof n === "number" && !isNaN(n) ? `$${n.toFixed(2)}` : "—";

// YYYY-MM-DD -> DD-MM-YY
const displayDDMMYY = (ymd?: string | null) => {
  if (!ymd) return "";
  const [y, m, d] = (ymd || "").split("-");
  if (!y || !m || !d) return ymd || "";
  return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y.slice(-2)}`;
};

const toYMD = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const toDateInput = (d: Date) => {
  const z = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  return z.toISOString().slice(0, 10);
};
const parseDate = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const getMonday = (d: Date) => {
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(d.getDate() + n);
  return x;
};

const sum = (arr: Array<number | null | undefined>) =>
  arr.reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);

// ---------- helpers ----------
const getPassenger = (r: AnyRow) => {
  const nameFromTc = [r?.taxicaller_data?.Passenger_First_Name, r?.taxicaller_data?.Passenger_Surname]
    .filter(Boolean)
    .join(" ")
    .trim();
  const candidate = r?.passenger ?? r?.passenger_name ?? nameFromTc ?? "";
  return (candidate || "").trim();
};

const getPickup = (r: AnyRow) => {
  const fromTc = r?.taxicaller_data?.Pick_Up_Location
    ? String(r.taxicaller_data.Pick_Up_Location)
    : "";
  const candidate = r?.pickup ?? fromTc ?? "";
  return (candidate.split(",")[0] || "").trim(); // first part only
};
// ---------- end helpers ----------

interface Props {
  driverName: string;
}

const DriverPaymentReport: React.FC<Props> = ({ driverName }) => {
  const initialMonday = useMemo(() => getMonday(new Date()), []);
  const [mondayStr, setMondayStr] = useState<string>(() => toDateInput(initialMonday));
  const mondayDate = useMemo(() => parseDate(mondayStr), [mondayStr]);
  const sundayDate = useMemo(() => addDays(mondayDate, 6), [mondayDate]);
  const mondayYMD = useMemo(() => toYMD(mondayDate), [mondayDate]);
  const sundayYMD = useMemo(() => toYMD(sundayDate), [sundayDate]);

  const [loading, setLoading] = useState(false);
  const [rawRows, setRawRows] = useState<AnyRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // map of Job_ID -> Account_ID for cases where RPC didn't bring it
  const [accountMap, setAccountMap] = useState<Record<string, string>>({});

  // fetch via RPC (lookback window)
  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.rpc(RPC_NAME, {
        p_driver_name: driverName,
        p_days: RPC_LOOKBACK_DAYS,
      });
      if (error) throw error;
      setRawRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || "Failed to load data");
      setRawRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverName]);

  // filter rows to selected week & sort
  const rows = useMemo(() => {
    const inRange = (d?: string | null) => !!d && d >= mondayYMD && d <= sundayYMD;
    return [...rawRows]
      .filter((r) => inRange(r?.pu_date))
      .sort((a, b) => {
        const aKey = `${a?.pu_date ?? ""}T${(a?.pu_time ?? "").slice(0, 8)}`;
        const bKey = `${b?.pu_date ?? ""}T${(b?.pu_time ?? "").slice(0, 8)}`;
        return aKey.localeCompare(bKey);
      });
  }, [rawRows, mondayYMD, sundayYMD]);

  // If any rows are missing Account_ID, fetch from taxicaller_data
  useEffect(() => {
    const needJobIds = rows
      .filter((r) => !(r?.taxicaller_data?.Account_ID))
      .map((r) => String(r?.taxicaller_job_id || ""))
      .filter(Boolean);

    if (needJobIds.length === 0) return;

    (async () => {
      const { data, error } = await supabase
        .from("taxicaller_data")
        .select("Job_ID, Account_ID")
        .in("Job_ID", needJobIds);

      if (error) return;

      const map: Record<string, string> = {};
      (data || []).forEach((row: any) => {
        if (row?.Job_ID && row?.Account_ID) {
          map[String(row.Job_ID)] = String(row.Account_ID);
        }
      });
      if (Object.keys(map).length) setAccountMap((prev) => ({ ...prev, ...map }));
    })();
  }, [rows]);

  const getAccountStrict = (r: AnyRow) => {
    const fromRpc = r?.taxicaller_data?.Account_ID;
    if (fromRpc) return String(fromRpc).trim();
    const byMap = accountMap[String(r?.taxicaller_job_id || "")];
    return byMap ? String(byMap).trim() : "";
  };

  const totals = useMemo(() => {
    return {
      jobs: rows.length,
      paid: sum(rows.map((r) => Number(r?.paid_amount ?? 0))),
      cash: sum(rows.map((r) => Number(r?.cash_amt ?? 0))),
      eft: sum(rows.map((r) => Number(r?.eftpos_amt ?? 0))),
      tm: sum(rows.map((r) => Number(r?.totalmobility_amt ?? 0))),
      tmh: sum(rows.map((r) => Number(r?.tm_hoist_amt ?? 0))),
      acc: sum(rows.map((r) => Number(r?.account_amt ?? 0))),
      other: sum(rows.map((r) => Number(r?.other_amt ?? 0))),
      disc: sum(rows.map((r) => Number(r?.discount ?? 0))),
      ecard: sum(rows.map((r) => Number(r?.ecard_amt ?? 0))),
    };
  }, [rows]);

  const downloadCsv = () => {
    const header = [
      "Date","Time","Passenger","Pick Up","$","Job #","A#",
      "C","E","TM","TMH","ACC","Other","DISC","eCard",
    ];

    const toCsvVal = (v: any) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[\",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const lines = [header.map(toCsvVal).join(",")];

    rows.forEach((r) => {
      lines.push(
        [
          formatDate(r?.pu_date ?? ""),
          String(r?.pu_time ?? "").slice(0, 5),
          getPassenger(r),
          getPickup(r),
          r?.paid_amount ?? "",
          r?.taxicaller_job_id ?? "",
          getAccountStrict(r),
          r?.cash_amt ?? "",
          r?.eftpos_amt ?? "",
          r?.totalmobility_amt ?? "",
          r?.tm_hoist_amt ?? "",
          r?.account_amt ?? "",
          r?.other_amt ?? "",
          r?.discount ?? "",
          r?.ecard_amt ?? "",
        ].map(toCsvVal).join(",")
      );
    });

    lines.push(
      [
        "", "", "", "", totals.paid, "", "",
        totals.cash, totals.eft, totals.tm, totals.tmh,
        totals.acc, totals.other, totals.disc, totals.ecard,
      ].map(toCsvVal).join(",")
    );

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileSafeName = (driverName || "Driver").replace(/\s+/g, "_");
    a.download = `Driver_Report_${fileSafeName}_${displayDDMMYY(toYMD(mondayDate))}_to_${displayDDMMYY(toYMD(sundayDate))}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onPickWeek = (val: string) => {
    const chosen = parseDate(val);
    const mon = getMonday(chosen);
    setMondayStr(toDateInput(mon));
  };
  const prevWeek = () => setMondayStr(toDateInput(addDays(mondayDate, -7)));
  const nextWeek = () => setMondayStr(toDateInput(addDays(mondayDate, 7)));
  const thisWeek = () => setMondayStr(toDateInput(getMonday(new Date())));
  const niceRange = `${formatDate(toYMD(mondayDate))} → ${formatDate(toYMD(sundayDate))}`;

  return (
    // 👇 One outer wrapper that’s 110% wide and centered.
    <div className="relative left-1/2 -translate-x-1/2 w-[100%] space-y-4">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end justify-between">
        <div className="space-y-1">
          <div className="text-lg font-semibold">Driver Reporting</div>
          <div className="text-sm text-muted-foreground">
            Showing week: <span className="font-medium">{niceRange}</span>
          </div>
        </div>

        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground mb-1">Week start (Monday)</label>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-primary" />
              <Input
                type="date"
                value={mondayStr}
                onChange={(e) => onPickWeek(e.target.value)}
                className="w-[170px] bg-background"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={prevWeek} variant="outline" className="border-yellow-600 text-yellow-400 hover:bg-yellow-900" title="Previous week">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button onClick={thisWeek} variant="outline" className="border-yellow-600 text-yellow-400 hover:bg-yellow-900" title="This week">
              This Week
            </Button>
            <Button onClick={nextWeek} variant="outline" className="border-yellow-600 text-yellow-400 hover:bg-yellow-900" title="Next week">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <Button onClick={downloadCsv} className="bg-yellow-600 hover:bg-yellow-700 text-black">
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden border border-border rounded-lg">
        <div className="max-h-[72vh] overflow-auto relative">
          <table className="min-w-[880px] w-full text-xs">
            <thead className="sticky top-0 z-10 bg-neutral-900/95 backdrop-blur">
              <tr className="text-center">
                <th className="px-2 py-2">Date</th>
                <th className="px-2 py-2">Time</th>
                <th className="px-2 py-2">Passenger</th>
                <th className="px-2 py-2">Pick Up</th>
                <th className="px-2 py-2">$</th>
                <th className="px-2 py-2">Job #</th>
                <th className="px-2 py-2">A#</th>
                <th className="px-2 py-2">C</th>
                <th className="px-2 py-2">E</th>
                <th className="px-2 py-2">TM</th>
                <th className="px-2 py-2">TMH</th>
                <th className="px-2 py-2">ACC</th>
                <th className="px-2 py-2">Other</th>
                <th className="px-2 py-2">DISC</th>
                <th className="px-2 py-2">eCard</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={15} className="p-4 text-center text-muted-foreground">Loading…</td>
                </tr>
              )}
              {error && !loading && (
                <tr>
                  <td colSpan={15} className="p-4 text-center text-red-400">{error}</td>
                </tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={15} className="p-4 text-center text-muted-foreground">No jobs in this week.</td>
                </tr>
              )}

              {rows.map((r) => (
                <tr key={r.id ?? r.taxicaller_job_id} className="odd:bg-background even:bg-muted/10">
                  <td className="px-2 py-1 text-center whitespace-nowrap">{formatDate(r?.pu_date)}</td>
                  <td className="px-2 py-1 text-center">{String(r?.pu_time ?? "").slice(0, 5)}</td>
                  <td className="px-2 py-1">{getPassenger(r)}</td>
                  <td className="px-2 py-1">{getPickup(r)}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.paid_amount ?? 0))}</td>
                  <td className="px-2 py-1 text-center">{r?.taxicaller_job_id ?? ""}</td>
                  <td className="px-2 py-1 text-center">{getAccountStrict(r)}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.cash_amt ?? 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.eftpos_amt ?? 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.totalmobility_amt ?? 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.tm_hoist_amt ?? 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.account_amt ?? 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.other_amt ?? 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.discount ?? 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtMoney(Number(r?.ecard_amt ?? 0))}</td>
                </tr>
              ))}
            </tbody>

            {/* Sticky totals footer — lighter grey to stand out */}
            <tfoot className="sticky bottom-0 z-10 bg-neutral-800/95 backdrop-blur border-t border-neutral-700">
              <tr className="font-semibold">
                <td className="px-2 py-2 text-left" colSpan={1}>
                  Jobs: {totals.jobs}
                </td>
                <td className="px-2 py-2" colSpan={3}></td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.paid)}</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.cash)}</td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.eft)}</td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.tm)}</td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.tmh)}</td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.acc)}</td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.other)}</td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.disc)}</td>
                <td className="px-2 py-2 text-right">{fmtMoney(totals.ecard)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DriverPaymentReport;
